/**
 * Geometry laws for the map overlays: how big a diamond is, and how far a card may sit from its
 * point (`V126`, `V128`, `V130`, P23, P24, P26).
 *
 * **Its own module because both `renderer.ts` and the tests need the law, and only one of them may
 * import deepscatter** (`oop_contracts §8`, enforced by `boundary.test.ts`). A test that
 * re-implemented the law would drift from the renderer silently and keep passing.
 *
 * ## The marker is the size of a point, because that is what was asked for
 *
 * Three sizings were tried in three phases, and the arithmetic is the whole story:
 *
 * | | at k = 1 | at k = 54 |
 * |---|---|---|
 * | a corpus point | 2 px | 8.1 px |
 * | `V123` — fixed 0.6rem | 13.6 px | 13.6 px |
 * | `V124` — the cloud's own rate on that base | 13.6 px | 55 px |
 * | `V125` — half the rate, capped 1.3x | 13.6 px | 17.7 px |
 * | **this** — the point's own size | **2 px** | **8.1 px** |
 *
 * `V124` and `V125` both kept `V123`'s 13.6 px base and argued about the *rate*. The base was the
 * problem: it is about **seven times a point's width**, and the reader asked twice for smaller
 * before asking for this directly.
 *
 * ## The law, from deepscatter's own shader
 *
 * ```glsl
 * size_multiplier = u_base_size * mix(last_size_multiplier, size_multiplier, ease);
 * point_size_adjust = exp(log(u_k) * u_zoom_balance);
 * gl_PointSize = point_size_adjust * size_multiplier;
 * ```
 *
 * `u_base_size` is `prefs.point_size` — 2 in this app's `plotAPI` call — and with no size channel
 * bound the multiplier is 1. So a point is `point_size * k^zoom_balance`.
 *
 * **`gl_PointSize` is in device pixels**, and regl sizes its drawing buffer by
 * `window.devicePixelRatio`, so the CSS size of a point is that divided by the ratio. The divisor
 * is **reasoned from regl's default, not measured** — this machine runs at ratio 1, where it is a
 * no-op.
 *
 * ## What is given up, stated
 *
 * `V123` argued the marker must be a *shape* rather than a colour, because the reader can point
 * the colour channel at any column so no colour is reliably unused. **At 2 px a rotated square is
 * not a shape**, so that argument no longer holds.
 *
 * **It was briefly the 1 px `--text` outline that carried the distinction, and the outline came off
 * at `V127`** on the reader's judgement of how it looked. What separates a generated point from a
 * corpus one now is shape at zoom and nothing at all zoomed out. The full account of what that
 * costs, including the case `P21.2` had fixed, is in `app.css` beside `.decoded-glyph` — this
 * paragraph asserted the opposite until P26 and was the last place in the tree that still did.
 *
 * **And there are three glyph classes now, not two** (`V137`, P29). A typed sequence is drawn as a
 * triangle at this same size, so the map carries corpus circles, generated diamonds and typed
 * triangles — all of which are the same handful of pixels below roughly k = 10. Two classes
 * colliding zoomed out is an ambiguity; three is a category the reader cannot resolve without
 * zooming. The size is settled and this does not reopen it; what carries the distinction instead
 * is the card, the panel entry, and the colour.
 */

/** deepscatter's own default exponent, and the fallback when its prefs cannot be read. */
export const DEFAULT_ZOOM_BALANCE = 0.35;

/** deepscatter's default `point_size`, and the fallback. This app's `plotAPI` call passes 2. */
export const DEFAULT_POINT_SIZE = 2;

/**
 * The smallest fill the diamond is drawn at, in CSS pixels.
 *
 * Only a floor against a sub-pixel marker — at `point_size` 2 and ratio 1 the law already gives 2,
 * so this binds only on a high-density display or if `point_size` is ever lowered.
 */
export const MARK_MIN_PX = 2;

/**
 * How far the invisible hit area extends beyond the fill, in CSS pixels.
 *
 * **A 2 px button cannot be clicked, and this marker has to take a left click to reopen and a
 * right click to delete.** The fill is what the reader sees; a transparent inset around it is what
 * the pointer finds. Sized so the target is at least ~18 px across at any zoom, which is the
 * smallest comfortable one.
 */
export const MARK_HIT_PADDING_PX = 8;

/**
 * The diamond's fill size in CSS pixels — the same size deepscatter draws a point.
 *
 * `pixelRatio` divides because `gl_PointSize` is in device pixels while CSS is not.
 */
export function markSizePx(
  k: number,
  balance: number = DEFAULT_ZOOM_BALANCE,
  pointSize: number = DEFAULT_POINT_SIZE,
  pixelRatio = 1,
): number {
  const exponent = Number.isFinite(balance) ? balance : DEFAULT_ZOOM_BALANCE;
  const base = Number.isFinite(pointSize) && pointSize > 0 ? pointSize : DEFAULT_POINT_SIZE;
  const ratio = Number.isFinite(pixelRatio) && pixelRatio > 0 ? pixelRatio : 1;
  const device = base * Math.exp(Math.log(Math.max(k, 1)) * exponent);
  return Math.max(MARK_MIN_PX, device / ratio);
}

// -- how far a card may be dragged from its point (`V128`, `V130`, P24, P26) ---------------

/**
 * The drag box at zoom 1, in CSS pixels: **how far the card may sit from its point.**
 *
 * The cards are draggable so a reader can arrange them for a screenshot, and bounded so one cannot
 * be flung somewhere that no longer reads as belonging to its point.
 *
 * **This is a gap, not a displacement** (`V130`). It bounds the distance from the point to the
 * nearest edge of the card, in whichever direction the card lies. `V128` bounded the displacement
 * from the card's *resting* position instead, and that resting position is down and to the right of
 * the point by half a card — so the reachable region was a box around a corner of the card rather
 * than around the point, and the card could never be put on the point's left or above it. Measured
 * before the change, on a 384 x 70 px card resting 8 px down and right of its point, with the
 * card's **centre** given relative to the point:
 *
 * | | left of the point | right of it | above it | below it |
 * |---|---|---|---|---|
 * | `V128`, limit 60 | never | 140 to 260 px | never | -17 to 103 px |
 * | **this**, limit 60 | up to 252 px | up to 252 px | up to 95 px | up to 95 px |
 *
 * `V128`'s box was symmetric in the *offset*, which is why measuring the offset found nothing
 * wrong with it. The offset is symmetric by construction; the card's position is the thing the
 * reader sees, and it was not.
 *
 * **Halved from 120 px on the reader's judgement** (`V129`), with the cap halved with it.
 */
export const DRAG_LIMIT_BASE_PX = 60;

/**
 * The widest the box ever gets, whatever the zoom. Reached at k = 4.
 *
 * At `V128`'s 480 px a card could be dragged most of a screen height away from its point, which is
 * what produced "seems unlimited at the bottom".
 */
export const DRAG_LIMIT_MAX_PX = 240;

/**
 * How much clearance `.pinned-point.flip-x` / `.flip-y` put between a flipped card and its anchor.
 *
 * **A stylesheet number the renderer has to know**, and the only one. A pinned card flips by
 * translating *itself* (`--flip-x: calc(-100% - 1rem)`), and a `translate` is invisible to every
 * layout read — `offsetLeft` still reports the unflipped position. So the drag bound would be
 * computed for a card on the right while the card is drawn on the left. A decoded card needs no
 * such correction: it flips by swapping `left` for `right`, which `offsetLeft` reports honestly.
 */
export const FLIP_CLEARANCE_PX = 16;

/**
 * How far a card may be dragged from its point at zoom `k`, in CSS pixels.
 *
 * **Linear in `k`, because that is how the distance between two points grows.** Zoomed out, the
 * cloud is dense and a card 300 px from its anchor could plausibly belong to any of a hundred
 * points; zoomed in, its neighbours are far away and the same 300 px is unambiguous. So the bound
 * is what the reader asked for — a box that scales with the zoom — rather than a constant.
 *
 * **The bound clamps what is *drawn*, never what is stored.** Zooming out therefore pulls a card
 * back towards its point, and zooming in again restores the arrangement exactly. Writing the
 * clamped value back would silently destroy a layout on one stray scroll.
 */
export function dragLimitPx(k: number): number {
  return Math.min(DRAG_LIMIT_MAX_PX, DRAG_LIMIT_BASE_PX * Math.max(1, k));
}

/** The offsets one axis of a drag may take, in CSS pixels from the card's resting position. */
export interface DragRange {
  readonly lo: number;
  readonly hi: number;
}

/**
 * The range one axis of a drag may take, so that the card stays within `limit` of its point.
 *
 * @param rest  where the card's near edge rests on this axis, measured from the point. Positive
 *              when the card rests after the point, which it does on both axes by default, and
 *              negative once the card has been flipped to the other side of its anchor.
 * @param size  the card's own width or height on this axis. **The whole of why the range is not
 *              symmetric:** to put the card on the other side of the point, the reader has to drag
 *              it across its own body first, so the two ends of the range differ by `size`.
 * @param limit how far the card may stray, from `dragLimitPx`.
 *
 * Equivalently, and this is the form to check the centring against: the card's **centre** may go
 * anywhere within `size / 2 + limit` of the point, which is a box centred on the point.
 */
export function dragRange(rest: number, size: number, limit: number): DragRange {
  return { lo: -(rest + size + limit), hi: limit - rest };
}

/** Clamp one axis of a drag offset into the range `dragRange` allows. */
export const clampDrag = (value: number, range: DragRange): number =>
  Math.min(range.hi, Math.max(range.lo, value));

/**
 * How far the pointer must travel before a press becomes a drag rather than a click.
 *
 * The cards carry a close control and per-residue spans that open a structure card, so a press
 * that does not move must still reach them (`P18.1`, `V112`).
 */
export const DRAG_THRESHOLD_PX = 4;

/**
 * How much of a card's bottom-right corner belongs to the browser's own resize grip.
 *
 * `V118` gave the cards `resize: horizontal`, which the user agent draws in that corner and which
 * fires an ordinary `pointerdown` on the element. Without this, dragging the grip moves the card
 * instead of resizing it, and the widen-to-fit-a-16-mer gesture stops working.
 */
export const DRAG_RESIZE_CORNER_PX = 18;
